import { useMemo } from "react";
import { getChildByNodeName } from "@repo/shared-utils/common-utils";
import {
  mapStorageCodeToLocale,
  SUPPORTED_LANGUAGE_PREFIXES,
} from "@repo/shared-utils/languageUtils";
import {
  FooterChild,
  FooterLanguageItem,
  FooterSectionKey,
  panelTitleBySectionKey,
  defaultSectionTitleByKey,
} from "./GlobalFooter.types";

const getLanguageLabel = (item: FooterLanguageItem) => {
  return (
    item.languageDropDownLabel ??
    `${item.languageDisplayLabel ?? item.languageLabel ?? ""} - ${(item.languageCode ?? "").toUpperCase()}`
  );
};

const buildLanguagePath = (languageCode: string, pathname: string) => {
  const code = languageCode.toLowerCase();
  const segments = pathname.split("/").filter(Boolean);
  const firstSeg = segments[0]?.toLowerCase() ?? "";
  const hasLangPrefix = (SUPPORTED_LANGUAGE_PREFIXES as readonly string[]).includes(firstSeg);
  const rest = hasLangPrefix ? segments.slice(1) : segments;

  if (code === "en" || code === "") {
    return rest.length > 0 ? `/${rest.join("/")}` : "/";
  }

  const targetPrefix = mapStorageCodeToLocale(code);
  return rest.length > 0 ? `/${targetPrefix}/${rest.join("/")}` : `/${targetPrefix}`;
};

export interface UseFooterDataResult {
  downloadAppText?: string;
  copyRightText?: string;
  corporateLinksTitle?: string;
  yearText?: string;
  companyNameImage?: string;
  corporateSection: FooterChild[];
  appDownload: FooterChild[];
  corporateLinks: FooterChild[];
  sectionTitles: Record<FooterSectionKey, string>;
  loanLinks: FooterChild[];
  reachUsData: FooterChild[];
  socialMediaIcons: { titleText: string; socialMediaUrl: string }[];
  calculatorData: FooterChild[];
  languagesData: { linkTitle: string; linkUrl: string; nodeName?: string }[];
  importantLinksData: {
    linkTitle: string;
    linkUrl: string;
    nodeName?: string;
    newTab?: string;
  }[];
  productsData: {
    sectionTitle?: string;
    titleUrl?: string;
    links: { linkTitle?: string; linkUrl?: string }[];
  }[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFooterData = (content: any, pathname: string): UseFooterDataResult => {
  return useMemo(() => {
    const defaultResult: UseFooterDataResult = {
      corporateSection: [],
      appDownload: [],
      corporateLinks: [],
      sectionTitles: defaultSectionTitleByKey,
      loanLinks: [],
      reachUsData: [],
      socialMediaIcons: [],
      calculatorData: [],
      languagesData: [],
      importantLinksData: [],
      productsData: [],
    };

    if (!content) return defaultResult;

    const { downloadAppText, copyRightText, corporateLinksTitle, yearText, companyNameImage } =
      content;

    const corporateSection =
      content?.children?.find((item: FooterChild) => item.nodeName === "corporateSection")
        ?.children ?? [];

    const appDownload = getChildByNodeName(content, "appDownload") as FooterChild[];
    const corporateLinks = getChildByNodeName(content, "corporateLinks") as FooterChild[];

    const footerChildren: FooterChild[] = Array.isArray(content?.children)
      ? (content.children as unknown as FooterChild[])
      : [];

    const accordionSections = footerChildren.filter(
      (item) =>
        item.isAccordian === "true" ||
        item.enableHeaderLanguage === "true" ||
        typeof item["cq:panelTitle"] === "string"
    );

    const hasChildNode = (item: FooterChild | undefined, nodeName: string) => {
      if (!item || !Array.isArray(item.children)) return false;
      return item.children.some((child) => child.nodeName === nodeName);
    };

    const hasNestedSectionLinks = (item: FooterChild | undefined) => {
      if (!item?.children) return false;

      const columnLinksNode = item.children.find((child) => child.nodeName === "columnLinks");
      if (!columnLinksNode?.children) return false;

      const stack: FooterChild[] = [...columnLinksNode.children];

      while (stack.length) {
        const current = stack.pop();
        if (current?.nodeName === "sectionLinks") return true;
        if (current?.children) stack.push(...current.children);
      }

      return false;
    };

    const hasLanguageMapping = (item: FooterChild | undefined) => {
      if (!item || !Array.isArray(item.children)) return false;

      return item.children.some(
        (child) =>
          (child.nodeName === "languageCodeMapping" || child.nodeName === "languages") &&
          Array.isArray(child.children)
      );
    };

    const isReachUsSection = (item: FooterChild | undefined) => hasChildNode(item, "socialMedia");
    const isProductsSection = (item: FooterChild | undefined) => hasNestedSectionLinks(item);

    const sequenceSections = accordionSections.filter(
      (item) => !hasLanguageMapping(item) && !isProductsSection(item) && !isReachUsSection(item)
    );

    const [applicationFormsSection, calculatorsSection, importantLinksSection] = sequenceSections;

    const sectionByKey: Partial<Record<FooterSectionKey, FooterChild>> = {
      languages: accordionSections.find((item) => hasLanguageMapping(item)),
      applicationForms: applicationFormsSection,
      productsPortfolio: accordionSections.find((item) => isProductsSection(item)),
      calculators: calculatorsSection,
      importantLinks: importantLinksSection,
      reachUs: accordionSections.find((item) => isReachUsSection(item)),
    };

    const getSectionByKey = (key: FooterSectionKey) => {
      if (sectionByKey[key]) {
        return sectionByKey[key];
      }

      if (key === "languages") return undefined;

      return accordionSections.find(
        (item) => item["cq:panelTitle"] === panelTitleBySectionKey[key]
      );
    };

    const getSectionTitle = (key: FooterSectionKey) => {
      const section = getSectionByKey(key);
      return typeof section?.columnTitle === "string"
        ? section.columnTitle
        : defaultSectionTitleByKey[key];
    };

    const getColumnLinksBySection = (key: FooterSectionKey, nodeName: string) => {
      const section = getSectionByKey(key);
      return section?.children?.find((child) => child.nodeName === nodeName)?.children ?? [];
    };

    const getFooterLanguageItems = () => {
      const section = getSectionByKey("languages");
      if (!section || !Array.isArray(section.children)) return [];

      const languageNode = section.children.find(
        (child) =>
          (child.nodeName === "languageCodeMapping" || child.nodeName === "languages") &&
          Array.isArray(child.children)
      );

      return (languageNode?.children ?? []) as FooterLanguageItem[];
    };

    const extractLinksFromSectionItem = (sectionItem: FooterChild) => {
      const sectionTitle = sectionItem.sectionTitle;
      const titleUrl = sectionItem.titleUrl;
      const sectionLinksNode = (sectionItem.children ?? []).find(
        (c) => c.nodeName === "sectionLinks"
      );
      const links = (sectionLinksNode?.children ?? []).map((link) => ({
        linkTitle: link.linkTitle,
        linkUrl: link.linkUrl,
      }));
      return {
        sectionTitle,
        titleUrl,
        links,
      };
    };

    const extractColumnListArr = (item: FooterChild) =>
      item.children?.filter((c) => c.nodeName === "columnList") ?? [];
    type ProductLinkGroup = ReturnType<typeof extractLinksFromSectionItem>;

    const getColumnLinksNode = (section: FooterChild): FooterChild | undefined => {
      if (!Array.isArray(section.children)) return undefined;
      return section.children.find(
        (child) => child.nodeName === "columnLinks" && Array.isArray(child.children)
      );
    };

    const mapColumnListToLinks = (columnList: FooterChild): ProductLinkGroup[] =>
      (columnList.children ?? []).map(extractLinksFromSectionItem);

    const mapPortfolioItemToLinks = (item: FooterChild): ProductLinkGroup[] => {
      const columnListArr = extractColumnListArr(item);
      return columnListArr.flatMap(mapColumnListToLinks);
    };

    const getProductsPortfolioData = (): ProductLinkGroup[] => {
      const section = getSectionByKey("productsPortfolio");
      if (!section || !Array.isArray(section.children)) return [];

      const columnLinksNode = getColumnLinksNode(section);
      if (!columnLinksNode || !Array.isArray(columnLinksNode.children)) return [];

      return columnLinksNode.children.flatMap(mapPortfolioItemToLinks);
    };

    const sectionTitles = {
      languages: getSectionTitle("languages"),
      applicationForms: getSectionTitle("applicationForms"),
      productsPortfolio: getSectionTitle("productsPortfolio"),
      calculators: getSectionTitle("calculators"),
      importantLinks: getSectionTitle("importantLinks"),
      reachUs: getSectionTitle("reachUs"),
    };

    const applicationFormLinks = getColumnLinksBySection("applicationForms", "columnLinks");
    const hasNestedChildren = applicationFormLinks.some(
      (item) => Array.isArray(item.children) && item.children.length > 0
    );
    const loanLinks = hasNestedChildren
      ? applicationFormLinks
      : [{ children: applicationFormLinks }];

    const reachUsData = getColumnLinksBySection("reachUs", "columnLinks");
    const socialMediaIcons = getColumnLinksBySection("reachUs", "socialMedia").map((item) => ({
      titleText: item.titleText ?? item.linkTitle ?? item.sectionTitle ?? item.nodeName ?? "",
      socialMediaUrl: item.socialMediaUrl ?? item.linkUrl ?? "",
    }));
    const calculatorData = getColumnLinksBySection("calculators", "columnLinks");
    const footerLanguageItems = getFooterLanguageItems();
    const languagesData = footerLanguageItems.map((item) => ({
      linkTitle: getLanguageLabel(item),
      linkUrl:
        item.vanityPath ??
        item.redirectionUrl ??
        buildLanguagePath(item.languageCode ?? "en", pathname),
      nodeName: item.nodeName,
    }));
    const importantLinksData = getColumnLinksBySection("importantLinks", "columnLinks").map(
      (item) => ({
        linkTitle: item.linkTitle ?? "",
        linkUrl: item.linkUrl ?? "",
        nodeName: item.nodeName,
        newTab: item.newTab,
      })
    );
    const productsData = getProductsPortfolioData() ?? [];

    return {
      downloadAppText,
      copyRightText,
      corporateLinksTitle,
      yearText,
      companyNameImage,
      corporateSection,
      appDownload,
      corporateLinks,
      sectionTitles,
      loanLinks,
      reachUsData,
      socialMediaIcons,
      calculatorData,
      languagesData,
      importantLinksData,
      productsData,
    };
  }, [content, pathname]);
};
